import React, { Fragment, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { Button, IconButton, Box, Typography, TextField } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { toggleDialog, toggleControl } from '../../../../store/control/duck';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import moment from 'moment';
import { scrollAndFocusTextareaEnd } from '../../../../utils/dom';
import QuickNotesDialog from '../../QuickNotesDialog/QuickNotesDialog';
import RecordMeetingRemarkButton from '../../../RecordMeetingRemarkButton/RecordMeetingRemarkButton';
import { getRemarkDateLine } from '../../../../utils/client';
import { GPTProductRecommendationButton } from '../../ChatGPTPresetDrawer/GPTProductRecommendation/GPTProductRecommendation';
import { ChatGPTPersonalAnalysisTextButton } from '../../ChatGPTPresetDrawer/ChatGPTPersonalAnalysis/ChatGPTPersonalAnalysis';
import CampaignCodeControl from '../../../CampaignCodeControl/container';
import { getLatestRemark } from '../../../../utils/remarks/get-latest-remark';
import FileText from 'lucide-react/dist/esm/icons/file-text';

const useStyles = makeStyles()(theme => ({
  notionContainer: {
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    overflow: 'hidden',
    boxSizing: 'border-box',
    marginTop: '12px',
    '&:hover': {
      borderColor: '#cbd5e1'
    }
  },
  notionContainerFocused: {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 2px ${theme.palette.primary.main}1a` // 1a is 10% opacity in hex
  },
  headerToolbar: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: '6px 12px',
    borderBottom: '1px solid #f1f5f9',
    backgroundColor: '#ffffff',
    userSelect: 'none'
  },
  headerTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#475569',
    lineHeight: '28px'
  },
  buttonGroup: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '8px',
    marginLeft: '12px'
  },
  actionButton: {
    padding: '0px 6px',
    fontSize: '12px',
    minWidth: 'unset',
    textTransform: 'none',
    fontWeight: 500,
    margin: 0,
    '&:hover': {
      backgroundColor: '#f1f5f9'
    }
  },
  textareaField: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#fffbeb',
    '& .MuiInputBase-root': {
      padding: '10px 12px',
      fontSize: '15.5px',
      lineHeight: '1.6',
      color: '#451a03',
      alignItems: 'flex-start',
      height: '100%',
      flexGrow: 1,
      boxSizing: 'border-box'
    },
    '& textarea': {
      height: '100% !important',
      overflowY: 'auto !important',
      color: '#451a03'
    }
  }
}));

const RemarkControl = props => {
  const {
    intl,
    remark,
    onChange,
    toggleDialog,
    toggleControl,
    InputProp,
    InputLabelProps,
    noLabelColorClass,
    noShrinkClass,
    recordMeetingRemarkButtonRef,
    enableSpeechToText,
    meetingDialog,
    setLoading,
    isRecruitment,
    onRunMeetingNotesAiAction,
    style,
    sx,
    className,
    ...rest
  } = props;

  const inputRef = useRef(null);
  const gptRecommendationRef = useRef(null);
  const [focused, setFocused] = useState(false);
  const { classes } = useStyles();

  useEffect(() => {
    const textarea = inputRef.current?.querySelector('textarea');
    if (!textarea) return;

    const note = remark || '';
    const dateLines = [...note.matchAll(/^📅\s\d{2}-[A-Za-z]{3}-\d{4}/gm)];
    const latestDateLine = dateLines[dateLines.length - 1];
    if (!latestDateLine) {
      textarea.scrollTop = textarea.scrollHeight;
      return;
    }

    const style = window.getComputedStyle(textarea);
    const mirror = document.createElement('div');
    const marker = document.createElement('span');
    Object.assign(mirror.style, {
      position: 'fixed',
      left: '-9999px',
      top: '0',
      visibility: 'hidden',
      whiteSpace: 'pre-wrap',
      overflowWrap: 'break-word',
      wordBreak: style.wordBreak,
      font: style.font,
      letterSpacing: style.letterSpacing,
      lineHeight: style.lineHeight,
      textIndent: style.textIndent,
      tabSize: style.tabSize,
      width: `${textarea.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0)}px`
    });
    mirror.append(document.createTextNode(note.slice(0, latestDateLine.index)));
    marker.style.cssText = `display:inline-block;width:0;height:${style.lineHeight}`;
    marker.textContent = '\u200b';
    mirror.append(marker, document.createTextNode(note.slice(latestDateLine.index)));
    document.body.append(mirror);
    textarea.scrollTop = marker.offsetTop;
    mirror.remove();
  }, []);

  const { style: InputPropsStyle, ...restInputProps } = InputProp || {};

  const appendRemark = text => {
    onChange({
      target: {
        value: `${remark}${remark ? `\n${!(remark || '').endsWith('\n') ? '\n' : ''}` : ''}${text}`
      }
    });
  };

  const appendVoiceRemark = text => {
    toggleDialog('meeting', {
      ...meetingDialog,
      remark: `${remark}${remark ? `\n${!(remark || '').endsWith('\n') ? '\n' : ''}` : ''}${text}`
    });
  };

  const expand = event => {
    event.stopPropagation();
    toggleDialog('quickNotes', {
      client: meetingDialog?.client,
      remark: remark,
      onSubmit: value => onChange({ target: { value: value } })
    });
  };

  const addDate = event => {
    event.stopPropagation();
    appendRemark(`📅 ${moment().format('DD-MMM-YYYY')}\n`);
    setTimeout(() => {
      if (inputRef.current) {
        const textareaElem = inputRef.current.querySelector('textarea');
        scrollAndFocusTextareaEnd(textareaElem);
      }
    }, 150);
  };

  const saveMeetingNotesAndRun = async action => {
    try {
      await onRunMeetingNotesAiAction?.(action);
    } catch {
      // Validation or save failed; keep the meeting dialog open.
    }
  };

  return (
    <Fragment>
      <QuickNotesDialog />
      <Box
        className={`${classes.notionContainer} ${focused ? classes.notionContainerFocused : ''} ${className || ''}`}
        style={style}
        sx={sx}
      >
        {/* Header Toolbar */}
        <Box className={classes.headerToolbar} onClick={event => event.preventDefault()}>
          <Box style={{ display: 'flex', alignItems: 'center', flexGrow: 1, flexWrap: 'wrap', gap: '8px' }}>
            <Box style={{ display: 'flex', alignItems: 'center', gap: '6px', marginRight: '4px' }}>
              <FileText size={16} style={{ color: '#d97706', verticalAlign: 'middle' }} />
              <Typography className={classes.headerTitle} style={{ margin: 0 }}>
                {intl.formatMessage({ id: 'Quick Notes' })}
              </Typography>
            </Box>
            <Button
              color="primary"
              variant="outlined"
              size="small"
              onClick={addDate}
              className={classes.actionButton}
            >
              {intl.formatMessage({ id: 'add-date' })}
            </Button>

            {enableSpeechToText && (
              <RecordMeetingRemarkButton
                ButtonProps={{
                  color: 'primary',
                  size: 'small',
                  style: { pointerEvents: 'auto' },
                  className: classes.actionButton
                }}
                IconProps={{
                  style: { fontSize: 16 }
                }}
                onResult={result => appendRemark(`${getRemarkDateLine()}${result}`)}
                ref={recordMeetingRemarkButtonRef}
              />
            )}

            {enableSpeechToText && isRecruitment && (
              <CampaignCodeControl targetCampaignCode={['FEATURE_RECRUIT']}>
                <RecordMeetingRemarkButton
                  ButtonProps={{
                    color: 'primary',
                    size: 'small',
                    style: { pointerEvents: 'auto' },
                    className: classes.actionButton
                  }}
                  IconProps={{
                    style: { fontSize: 16 }
                  }}
                  onResult={result => appendRemark(`${getRemarkDateLine()}${result}`)}
                  ref={recordMeetingRemarkButtonRef}
                  isRecruit={true}
                />
              </CampaignCodeControl>
            )}

            <CampaignCodeControl targetCampaignCode={['FEATURE_PRODUCT_RECOMMENDATION_CHUBB', 'FEATURE_GPT']}>
              {meetingDialog?.client && (
                <GPTProductRecommendationButton
                  ref={gptRecommendationRef}
                  useLoadingBackdrop={false}
                  loadingControl={setLoading}
                  toggleControl={toggleControl}
                  client={meetingDialog.client}
                  remark={remark}
                  language={intl.locale}
                >
                  <Button
                    onClick={() =>
                      saveMeetingNotesAndRun(() => gptRecommendationRef.current.handleCheckAndSend?.())
                    }
                    color="primary"
                    variant="outlined"
                    size="small"
                    className={classes.actionButton}
                  >
                    <img
                      style={{
                        width: '12px',
                        height: '12px',
                        paddingRight: '2px'
                      }}
                      src="/img/icons/ai_bg_free.svg"
                      alt=""
                    />
                    {intl.formatMessage({ id: 'gpt-product-recommendation-client-entry-title' })}
                  </Button>
                </GPTProductRecommendationButton>
              )}
            </CampaignCodeControl>

            <CampaignCodeControl targetCampaignCode={['FEATURE_GPT']}>
              <ChatGPTPersonalAnalysisTextButton
                intl={intl}
                size="small"
                style={{
                  padding: '0px 4px',
                  fontSize: '12px',
                  minWidth: 'unset',
                  textTransform: 'none',
                  fontWeight: 500
                }}
                onClick={() =>
                  saveMeetingNotesAndRun(() => {
                    toggleControl('ChatGPTPersonalAnalysis', {
                      client: meetingDialog?.client,
                      type: 'meeting_notes',
                      open: true,
                      payload: { remark: getLatestRemark(remark) }
                    });
                  })
                }
              />
            </CampaignCodeControl>
          </Box>
          <Box style={{ marginTop: '2px' }}>
            <IconButton
              size="medium"
              onClick={expand}
              style={{
                pointerEvents: 'auto',
                color: '#3966f8',
                padding: '4px'
              }}
            >
              <OpenInNewIcon style={{ fontSize: 22 }} />
            </IconButton>
          </Box>
        </Box>

        {/* Text Area */}
        <TextField
          variant="standard"
          fullWidth
          multiline
          value={remark}
          onChange={onChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          slotProps={{
            input: {
              disableUnderline: true,
              style: InputPropsStyle,
              ...restInputProps
            }
          }}
          className={classes.textareaField}
          ref={inputRef}
          {...rest}
        />
      </Box>
    </Fragment>
  );
};

const container = connect(state => ({}), {
  toggleDialog,
  toggleControl
})(RemarkControl);

export default injectIntl(container);
